import * as AmplifyProcessStates from '../process-states';
import * as commonEnums from '../../../enums/index';
import { DEFAULT_BENCH_EDITABLE } from '../state/code-bench';
import {
  AccelerateProcessStatus,
  CapabilityAvailabilityReason,
  ProcessQAStatus,
  ResourceId,
  ServiceLines
} from '../../../enums';
import { isResourceFullAccess } from '../resources';
import { capabilityValue } from './conditional-utilities';
import {
  isIPC as isIPCServiceline,
  isSurgery as isSurgeryServiceline,
  normalizeServiceLineName
} from '../../service-lines';
import { isEmpty } from '../../object-utils';
import * as cmxDateTime from '../../date-time/cmx-date-time';
const { ProcessStatusLabel, HoldReasonType, HoldReasonLabel } = commonEnums;
const {
  CodableStatus,
  PostCodableStatus,
  PreCodableStatus,
  CancelledStatus
} = AmplifyProcessStates;
export const determineQaEditable = (
  status: CMxCommonApp.ProcessInstanceStatus,
  qaStatus: CMxCommonApp.QaStatus,
  roles: string[]
) => {
  const codableQARoles = new Set<string>([
    'coordinator',
    'professional_services',
    'coding_manager'
  ]);
  const hasCodableQaRole = roles.some(role => codableQARoles.has(role));

  const isCaseInQAandCodable =
    qaStatus === ProcessQAStatus.IN_QA &&
    status === AccelerateProcessStatus.SUBMITTED &&
    hasCodableQaRole;

  return isCaseInQAandCodable;
};

export const determineBenchEditable = (
  processInstance:
    | AmplifyAPI.ProcessInstance
    | CMxCommonApp.benchEditableProcessProps,
  userContext: CMxAPI.OrganizationContext,
  roles: string[],
  parentCaserunUid: CMxAPI.NullableString,
  submitInProgress: boolean = false,
  canEditCase: boolean = true
): Accelerate.BenchEditableState => {
  const benchEditable = { ...DEFAULT_BENCH_EDITABLE };

  // Early return if processInstance is null/undefined to prevent errors
  if (!processInstance) {
    return {
      ...benchEditable,
      editable: false,
      waiting: false,
      submitted: false,
      cancelled: false
    };
  }

  const isCaseInQAandCodable = determineQaEditable(
    processInstance?.status,
    processInstance?.qaStatus,
    roles
  );
  /**If case is child case then not editable*/
  const isChildCase = !!parentCaserunUid;

  const showHoldSubmissionTimer =
    isWithinHoldSubmissionWindow(processInstance);

    benchEditable.editable =
    !submitInProgress &&
    processInstance?.isChargeable &&
    !processInstance.cancelled &&
    (processInstance?.qaStatus === ProcessQAStatus.IN_QA
      ? isCaseInQAandCodable
      : AmplifyProcessStates.CodableStatus.has(processInstance.status) ||
        showHoldSubmissionTimer) &&
    isResourceFullAccess(
      userContext?.resources ?? [],
      commonEnums.ResourceId.CODE_CASE
    ) &&
    !isChildCase &&
    processInstance.status !== AccelerateProcessStatus.REMOVED &&
    canEditCase;



  benchEditable.waiting = AmplifyProcessStates.PreCodableStatus.has(
    processInstance.status
  );
  benchEditable.submitted =
    !processInstance.cancelled &&
    AmplifyProcessStates.PostCodableStatus.has(processInstance.status);
  benchEditable.cancelled =
    processInstance.cancelled &&
    AmplifyProcessStates.CancelledStatus.has(processInstance.status);
  return benchEditable;
};

export const getImprovements = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  /** Previously available only for IPC, currently enabling it for all servicelines */
  const defaults = {
    value: true,
    reasons: reasons
  };

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

const isWithinHoldSubmissionWindow = (processInstance) => {
  if (!processInstance?.holdSubmissionDate) return false;

  const timeDuration = cmxDateTime.calculateDurationDateFns(
    cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE),
    processInstance?.holdSubmissionDate
  );

  const seconds =
    (timeDuration?.hours > 0 ? timeDuration?.hours : 0) * 60 +
    (timeDuration?.minutes > 0 ? timeDuration?.minutes : 0) * 60 +
    (timeDuration?.seconds > 0 ? timeDuration?.seconds : 0);

  return !!processInstance?.holdSubmission && seconds > 0;
};

export const getImprovementsAction = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const isDelayedSubmission =
    isWithinHoldSubmissionWindow(processInstance);
  const defaults = {
    value:
      benchEditable.editable &&
      processInstance?.isChargeable &&
      (!benchEditable.submitted ||
        (benchEditable.submitted &&
          (processInstance.qaStatus === ProcessQAStatus.IN_QA ||
            isDelayedSubmission
          ))
      ) &&
      !benchEditable.cancelled,
    reasons: reasons
  };

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const getRenderSubmitCase = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const displayFlag = benchEditable.editable && processInstance?.isChargeable;
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }

  const submitCase: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return submitCase;
};

export const getActionMenuCancelCapability = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState,
  actionMenu: CMxCommonApp.Capability,
  roles: string[],
  resources: CMxAPI.Resource[]
) => {
  const isCaseInQAandCodable = determineQaEditable(
    processInstance?.status,
    processInstance?.qaStatus,
    roles
  );

  const serviceLine = isIPCServiceline(processInstance?.serviceLine)
    ? ServiceLines.BEDSIDE_PRO
    : processInstance?.serviceLine;

  const hasVoidPermission = isResourceAllowed(
    { resources } as CMxAPI.OrganizationContext,
    ResourceId.VOID_CASE,
    serviceLine
  );

  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const displayFlag =
    capabilityValue(actionMenu.on) &&
    benchEditable.editable &&
    !isCaseInQAandCodable &&
    hasVoidPermission &&
    processInstance?.isChargeable;
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }

  const cancelCase: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return cancelCase;
};

export const getRenderPagination = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  const caseType = processInstance?.caseType;
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const displayFlag =
    caseType === commonEnums.UIModesEnum.PLAYGROUND && !benchEditable.editable;
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const pagination: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return pagination;
};

export const getRenderviewOnlyCase = (
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const displayFlag = !benchEditable.editable;
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const viewOnlyCase: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return viewOnlyCase;
};

export const getRenderHoldCase = (
  processInstance: AmplifyAPI.ProcessInstance,
  holdCase: CMxAPI.HoldCaseResponse,
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const isOffHold = holdCase.hold_reason === HoldReasonType.OFF_HOLD;
  const isCaseNotOnHold =
    holdCase.hold_reason === '' || holdCase.hold_reason === null;

  const displayFlag =
    processInstance?.isChargeable &&
    benchEditable.editable &&
    processInstance?.caseType === commonEnums.UIModesEnum.STANDALONE &&
    (isOffHold || isCaseNotOnHold) &&
    processInstance?.qaStatus !== ProcessQAStatus.IN_QA;

  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const getRenderMergeableCase = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const displayFlag =
    processInstance?.isChargeable &&
    benchEditable.editable &&
    processInstance?.caseType === commonEnums.UIModesEnum.STANDALONE;

  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const getRenderOffHoldCase = (
  processInstance: AmplifyAPI.ProcessInstance,
  holdCase: CMxAPI.HoldCaseResponse,
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const caseHoldStatus = new Set<string>([
    HoldReasonType.HOLD,
    HoldReasonType.HOLD_FOR_DOCUMENTATION_QUERY,
    HoldReasonType.HOLD_FOR_MANAGER_REVIEW
  ]);
  const displayFlag =
    processInstance?.isChargeable &&
    benchEditable.editable &&
    processInstance?.caseType === commonEnums.UIModesEnum.STANDALONE &&
    caseHoldStatus.has(holdCase.hold_reason);

  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const getToggleCaseComment = (
  removeHoldCase: CMxCommonApp.Capability,
  caseCommentMode: boolean
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  let displayFlag = capabilityValue(removeHoldCase.on) && caseCommentMode;
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

/** service line check */
export const isIPCOrSurgery = (processInstance: AmplifyAPI.ProcessInstance) => {
  const isIPC = isIPCServiceline(processInstance?.serviceLine);
  const isSurgery = isSurgeryServiceline(processInstance?.serviceLine);
  return isIPC || isSurgery;
};

/**
 * Check if user has permission to show exclude case based on their roles
 * @param userRoles - Array of user's role names
 * @param config - Configuration object with role permissions
 * @returns true if user has at least one role with showExcludeCase permission
 */
const canShowExcludeCase = (
  userRoles: string[],
  config: Record<string, any> | null
): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }
  
  if (!config || !config.availableRoles || !Array.isArray(config.availableRoles)) {
    return false;
  }

  // Check if user has any role that allows showing exclude case
  return userRoles.some(userRole => {
    const roleConfig = config.availableRoles.find(
      (r: any) => r.role_name === userRole
    );
    return roleConfig?.showExcludeCase === true;
  });
};

export const getRenderToggleChargeableCase = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState,
  userRoles: string[],
  excludeCasePermissionsConfig: Record<string, any> | null
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  // If no config is available, don't show exclude case option
  if (!excludeCasePermissionsConfig) {
    console.warn('[getRenderToggleChargeableCase] No config available - hiding exclude case option');
    return {
      on: { value: false, reasons: [CapabilityAvailabilityReason.CONFIGURATION] },
      actionable: { value: false, reasons: [CapabilityAvailabilityReason.CONFIGURATION] }
    };
  }

  // Check if user has permission based on their roles
  const hasShowExcludeCasePermission = canShowExcludeCase(userRoles, excludeCasePermissionsConfig);

  const displayFlag =
    hasShowExcludeCasePermission &&
    (benchEditable.editable || !processInstance?.isChargeable) &&
    !AmplifyProcessStates.NonCodableStatus.has(processInstance.status);
    
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  
  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};


export const getActionMenuCapability = (
  holdCase: CMxCommonApp.Capability,
  removedCaseHold: CMxCommonApp.Capability,
  toggleChargeableCase: CMxCommonApp.Capability,
  splitCapability: CMxCommonApp.Capability,
  lateCaseClearNotification: CMxCommonApp.Capability,
  processInstance: AmplifyAPI.ProcessInstance
) => {
  const displayFlag =
    (capabilityValue(holdCase.on) ||
      capabilityValue(removedCaseHold.on) ||
      capabilityValue(toggleChargeableCase.on) ||
      capabilityValue(splitCapability.on) ||
      capabilityValue(lateCaseClearNotification.on)) &&
    processInstance.status !== AccelerateProcessStatus.REMOVED;
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const getGeneralCancelCapability = (
  actionMenu: CMxCommonApp.Capability,
  cancelCase: CMxCommonApp.Capability,
  benchEditable: Accelerate.BenchEditableState,
  resources: CMxAPI.Resource[],
  activeServiceLineName: string
) => {
  const serviceLine = isIPCServiceline(activeServiceLineName)
    ? ServiceLines.BEDSIDE_PRO
    : activeServiceLineName;

  const hasVoidPermission = isResourceAllowed(
    { resources } as CMxAPI.OrganizationContext,
    ResourceId.VOID_CASE,
    serviceLine
  );

  const displayFlag =
    !capabilityValue(actionMenu.on) &&
    !capabilityValue(cancelCase.on) &&
    hasVoidPermission &&
    benchEditable.editable &&
    !benchEditable.cancelled;
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  if (!displayFlag) {
    reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }
  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const getCaseEditable = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  const isIPC = isIPCServiceline(processInstance?.serviceLine);
  const isSurgery = isSurgeryServiceline(processInstance.serviceLine);

  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const isEditable =
    (isIPC || isSurgery) &&
    processInstance?.caseType !== commonEnums.UIModesEnum.EPIC_EMBEDDED &&
    benchEditable.editable;
  const defaults = {
    value: isEditable,
    reasons: reasons
  };

  if (isEditable) {
    defaults.reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }
  const caseEdit: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return caseEdit;
};

export const getOldCaseLoadedAlert = (
  caseVersions: AmplifyAPI.CaseVersion[],
  processInstance: AmplifyAPI.ProcessInstance
) => {
  const currentVersionPosition =
    caseVersions?.findIndex(
      caseVersion =>
        caseVersion.processInstanceGuid === processInstance?.guid?.uuid
    ) ?? -1;

  const currentVersionObject =
    currentVersionPosition !== -1 ? caseVersions[currentVersionPosition] : null;

  const displayFlag =
    currentVersionObject?.processInstanceStatus ===
    commonEnums.AccelerateProcessStatus.REPLACED;

  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const getPreviousSentCaseAlert = (
  caseVersions: AmplifyAPI.CaseVersion[],
  processInstance: AmplifyAPI.ProcessInstance
) => {
  const currentVersionPosition =
    caseVersions?.findIndex(
      caseVersion =>
        caseVersion.processInstanceGuid === processInstance?.guid?.uuid
    ) ?? -1;

  const previousVersions = caseVersions?.filter(
    (_, index) => index < currentVersionPosition
  );

  const displayFlag = previousVersions?.some(
    previousVersion =>
      previousVersion.processInstanceStatus ===
        commonEnums.AccelerateProcessStatus.SUBMITTED ||
      previousVersion.processInstanceStatus ===
        commonEnums.AccelerateProcessStatus.CANCELLED ||
      previousVersion.processInstanceStatus ===
        commonEnums.AccelerateProcessStatus.SENT
  );

  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const defaults = {
    value: displayFlag,
    reasons: reasons
  };

  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

/**
 * Worklist "Next" / cursor navigation only. Other code-bench capabilities (case actions,
 * submit, menus, etc.) do not use this flag — Cases vs Worklist should match there.
 */
export const getServicedeskActions = (
  processInstance: AmplifyAPI.ProcessInstance,
  cursorAPIContext: CMxCommonApp.CursorAPIContext
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const hasWorklistQuery =
    cursorAPIContext.worklistContext?.query != null &&
    typeof cursorAPIContext.worklistContext.query === 'object';
  const value =
    cursorAPIContext.servicedeskMode === true && hasWorklistQuery;
  const defaults = {
    value,
    reasons: reasons
  };

  if (!cursorAPIContext.servicedeskMode || !hasWorklistQuery) {
    defaults.reasons.push(CapabilityAvailabilityReason.CONFIGURATION);
  }

  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const getNoteUnavailableReason = (activeItem: CMxAPI.CaseRun) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const defaults = {
    value: activeItem?.note_unavailable_reason ? true : false,
    reasons: reasons
  };

  const capability: CMxCommonApp.Capability = {
    on: { ...defaults },
    actionable: { ...defaults }
  };
  return capability;
};

export const determineProcessStatus = (
  processStatusProps: CMxCommonApp.ProcessStatusProps,
  isCaseInAmplify: boolean,
  parentCaserunUid: CMxAPI.NullableString
) => {
  let processStatus;
  const isCaseInQAandCodable = determineQaEditable(
    processStatusProps.processInstanceStatus,
    processStatusProps.qaStatus,
    processStatusProps.roles ?? []
  );
  const benchEditable =
    processStatusProps?.benchEditable ??
    determineBenchEditable(
      {
        status: processStatusProps.processInstanceStatus,
        qaStatus: processStatusProps.qaStatus,
        isChargeable: processStatusProps.isChargeable,
        cancelled: processStatusProps.cancelled
      },
      processStatusProps.userContext,
      processStatusProps.roles,
      parentCaserunUid,
      false
    );

  if (!isCaseInAmplify) {
    processStatus = ProcessStatusLabel.AUTOMATE;
  } else if (!processStatusProps.isChargeable) {
    processStatus = ProcessStatusLabel.NON_CHARGEABLE;
  } else if (
    (CancelledStatus.has(processStatusProps.processInstanceStatus) &&
      benchEditable.cancelled) ||
    processStatusProps.processInstanceStatus === AccelerateProcessStatus.REMOVED
  ) {
    processStatus = ProcessStatusLabel.VOIDED;
  } else if (isCaseInQAandCodable) {
    processStatus = ProcessStatusLabel.IN_QA;
  } else if (
    PostCodableStatus.has(processStatusProps.processInstanceStatus) &&
    benchEditable.submitted
  ) {
    processStatus = ProcessStatusLabel.SUBMITTED;
  } else if (
    processStatusProps.holdReason &&
    processStatusProps.holdReason !== HoldReasonType.OFF_HOLD
  ) {
    processStatus =
      HoldReasonLabel[processStatusProps.holdReason] ?? HoldReasonLabel.HOLD;
  } else if (PreCodableStatus.has(processStatusProps.processInstanceStatus)) {
    processStatus = ProcessStatusLabel.IMPORTING;
  } else if (CodableStatus.has(processStatusProps.processInstanceStatus)) {
    processStatus = ProcessStatusLabel.READY_FOR_CODING;
    if (processStatusProps?.holdReason === HoldReasonType.OFF_HOLD) {
      processStatus = processStatus + ': ' + HoldReasonLabel.OFF_HOLD;
    }
  }
  return processStatus;
};

export const fecthProcessStatusClasses = (
  processStatus: string,
  holdReason: string
) => {
  let styles = [
    'process-status-' +
      processStatus
        ?.toLowerCase()
        .replace(/documentation|query|manager|review|:|off hold/g, '')
        .trim()
        .replace(/ /g, '-')
  ];
  if (processStatus !== undefined) {
    styles.push('chip');
    styles.push('label');
    styles.push('process-status');
  }
  return styles;
};

export const getMergeActionMenuCapability = (
  benchEditable: Accelerate.BenchEditableState,
  userContext: CMxAPI.OrganizationContext,
  parentCase: boolean,
  activeItem: CMxAPI.CaseRun,
  activeServiceLineName: string
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const serviceLine = isIPCServiceline(activeServiceLineName)
    ? ServiceLines.BEDSIDE_PRO
    : activeServiceLineName;

  const hasMergeResource = isResourceAllowed(
    userContext,
    ResourceId.MERGE_CASE,
    serviceLine
  );

  const showMergeOption =
    hasMergeResource &&
    benchEditable?.editable &&
    !benchEditable.submitted &&
    !parentCase &&
    !!activeItem?.merge_key;

  const defaults = {
    value: showMergeOption,
    reasons: reasons
  };

  if (!showMergeOption) {
    reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const getSplitActionMenuCapability = (
  benchEditable: Accelerate.BenchEditableState,
  userContext: CMxAPI.OrganizationContext,
  parentCase: boolean,
  activeServiceLineName: string
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const serviceLine = isIPCServiceline(activeServiceLineName)
    ? ServiceLines.BEDSIDE_PRO
    : activeServiceLineName;

  const hasMergeResource = isResourceAllowed(
    userContext,
    ResourceId.MERGE_CASE,
    serviceLine
  );

  const showSplitOption =
    hasMergeResource &&
    benchEditable?.editable &&
    !benchEditable.submitted &&
    !!parentCase;
  const defaults = {
    value: showSplitOption,
    reasons: reasons
  };

  if (!showSplitOption) {
    reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const getCoderFeedbackCapability = (
  benchEditable: Accelerate.BenchEditableState,
  processInstance: AmplifyAPI.ProcessInstance,
  userContext: CMxAPI.OrganizationContext
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const serviceLine = isIPCServiceline(processInstance?.serviceLine)
    ? ServiceLines.BEDSIDE_PRO
    : processInstance?.serviceLine;

  const hasCoderFeedbackResource = isResourceAllowed(
    userContext,
    ResourceId.CODER_FEEDBACK,
    serviceLine
  );

  const showCoderFeedbackOption =
    hasCoderFeedbackResource &&
    benchEditable?.editable &&
    processInstance.qaStatus === ProcessQAStatus.IN_QA;
  const defaults = {
    value: showCoderFeedbackOption,
    reasons: reasons
  };

  if (!showCoderFeedbackOption) {
    reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const getAttachDocsCapability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.ATTACH_DOCS,
    activeServiceLineName
  );
};

// mapping all IPC SL names to BEDSIDE_PRO
export const updateServiceLinesIPC = (serviceLines: string[]): string[] =>
  serviceLines.map(serviceLine =>
    isIPCServiceline(serviceLine) ? ServiceLines.BEDSIDE_PRO : serviceLine
  );

export const getCreateCaseCapability = (
  userContext: CMxAPI.OrganizationContext,
  processInstance: AmplifyAPI.ProcessInstance
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const serviceLine = isIPCServiceline(processInstance?.serviceLine)
    ? ServiceLines.BEDSIDE_PRO
    : processInstance?.serviceLine;

  const hasCreateCaseResource = isResourceAllowed(
    userContext,
    ResourceId.CREATE_CASE,
    serviceLine
  );

  const showCreateCaseOption =
    hasCreateCaseResource && processInstance?.isChargeable;

  const defaults = {
    value: showCreateCaseOption,
    reasons: reasons
  };

  if (!hasCreateCaseResource) {
    reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const getTimelineInfiniteLoadCapability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.TIMELINE_INFINITE_LOAD,
    activeServiceLineName
  );
};

export const getProductivityTrackerCapability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.PRODUCTIVITY_TRACKER,
    activeServiceLineName
  );
};

export const getDiagnosisCodeSuggestionCapability = (
  userContext: CMxAPI.OrganizationContext,
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const serviceLine = isIPCServiceline(processInstance?.serviceLine)
    ? ServiceLines.BEDSIDE_PRO
    : processInstance?.serviceLine;

  const hasDiagnosisCodeSuggestion = isResourceAllowed(
    userContext,
    ResourceId.DIAGNOSIS_CODE_SUGGESTION,
    serviceLine
  );

  const showDiagnosisCodeSuggestion =
    isCaseEditAllowed(processInstance, benchEditable) &&
    hasDiagnosisCodeSuggestion;

  const defaults = {
    value: showDiagnosisCodeSuggestion,
    reasons: reasons
  };

  if (!hasDiagnosisCodeSuggestion) {
    reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const isCaseEditAllowed = (
  processInstance: AmplifyAPI.ProcessInstance,
  benchEditable: Accelerate.BenchEditableState
) => {
  return (
    benchEditable.editable &&
    processInstance?.isChargeable &&
    (!benchEditable.submitted ||
      (benchEditable.submitted &&
        processInstance.qaStatus === ProcessQAStatus.IN_QA)) &&
    !benchEditable.cancelled
  );
};

export const getCoderNotesCapability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.CODER_NOTES,
    activeServiceLineName
  );
};

export const getUseDataAccessAmplifyCapability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.USE_DATAACCESS_AMPLIFY,
    activeServiceLineName
  );
};


export const getTransactionV2Capability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const serviceLine = isIPCServiceline(activeServiceLineName)
    ? ServiceLines.BEDSIDE_PRO
    : activeServiceLineName;

  const hasTransactionV2 = isResourceAllowed(
    userContext,
    ResourceId.TRANSACTION_V2,
    serviceLine
  );

  const hasAmplifyV2 = isResourceAllowed(
    userContext,
    ResourceId.AMPLIFY_CASE_RUN_V2,
    serviceLine
  );

  const showTransactionV2 = hasTransactionV2 && hasAmplifyV2;

  const defaults = {
    value: showTransactionV2,
    reasons: reasons
  };

  if (!hasTransactionV2) {
    reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const getCodeSourcesFeaturesCapability = (
  userContext: CMxAPI.OrganizationContext
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];
  const hasCodeSourcesFeatures =
    userContext?.resources?.some(
      resource => resource.resourceName === ResourceId.MULTI_CODESET_FEATURE
    ) ?? false;

  const defaults = {
    value: hasCodeSourcesFeatures,
    reasons: reasons
  };

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

/**
 * To fetch capability to load case details with refresh as true
 * When this capability is enabled an extra API call is made with refresh as true
 * This eventually fetches latest data from XC
 * @param userContext
 * @returns
 */
export const getGetCaserunWithRefreshCapability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.GET_CASERUN_WITH_REFRESH,
    activeServiceLineName
  );
};

export const getAmplifyCaseRunV2Capability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.AMPLIFY_CASE_RUN_V2,
    activeServiceLineName
  );
};

export const getLateCaseClearNotificationCapability = (
  processInstance: AmplifyAPI.ProcessInstance
) => {
  const isLateCaseUpdate =
    processInstance?.status === AccelerateProcessStatus.LATE_CASE_UPDATE;
  const defaults = {
    value: isLateCaseUpdate,
    reasons: []
  };
  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

export const getActionBarSelectMultipleWorkitemsCapability = (
  roles: string[]
) => {
  const on = roles.includes('cqa_manager') ? true : false;
  return {
    on: {
      value: on,
      reasons: []
    },
    actionable: {
      value: on,
      reasons: []
    }
  };
};

/**
 * Verifies if a resource is allowed for a specific service line
 * Incudes BSP IPC service line check
 * Includes * check for all service lines
 * @param resource - The resource to verify
 * @param activeServiceLineName - The active service line name to check against
 * @returns True if the resource is allowed for the service line, false otherwise
 */
export const verifyServiceLine = (
  resource: CMxAPI.Resource,
  activeServiceLineName: string
) => {
  const selectedServiceLine = normalizeServiceLineName(activeServiceLineName ?? '');
  const isBSPactive = isIPCServiceline(selectedServiceLine);
  if (!!!resource || isEmpty(activeServiceLineName)) {
    return false;
  }
  return resource?.serviceLines?.some(
    serviceLine =>
      normalizeServiceLineName(serviceLine) === selectedServiceLine ||
      serviceLine === '*' ||
      (isBSPactive && isIPCServiceline(serviceLine))
  );
};

/**
 * Checks if a resource is allowed for a specific service line in the given context
 * @param activeContext - The organization context containing resources
 * @param resourceName - The name of the resource to check
 * @param activeServiceLineName - The active service line name to check against
 * @returns True if the resource is allowed for the service line, false otherwise
 */
export const isResourceAllowed = (
  activeContext: CMxAPI.OrganizationContext,
  resourceName: string,
  activeServiceLineName: string
) => {
  if (!!!resourceName) {
    return false;
  }
  // check if resource exists in context
  const [chosenResource] =
    activeContext?.resources?.filter(
      resource =>
        resource.resourceName.toLowerCase() === resourceName.toLowerCase()
    ) ?? [];
  // no, chosen resource found, always return false
  if (!chosenResource) {
    return false;
  }
  if (activeServiceLineName === '*') {
    return true;
  }
  return verifyServiceLine(chosenResource, activeServiceLineName);
};

/**
 * Determines capability availability for a resource based on service line permissions
 * @param activeContext - The organization context containing resources
 * @param resourceName - The name of the resource to check
 * @param activeServiceLineName - The active service line name to check against
 * @returns An object containing capability status with reasons for availability
 */
export const determineCapabilityByResource = (
  activeContext: CMxAPI.OrganizationContext,
  resourceName: string,
  activeServiceLineName: string
) => {
  const reasons: CMxCommonApp.CapabilityAvailabilityReason[] = [];

  const resourcePermitted = isResourceAllowed(
    activeContext,
    resourceName,
    activeServiceLineName
  );

  const defaults = {
    value: resourcePermitted,
    reasons: reasons
  };

  if (!resourcePermitted) {
    reasons.push(CapabilityAvailabilityReason.PRODUCT_EXCLUDED);
  }

  return {
    on: { ...defaults },
    actionable: { ...defaults }
  };
};

/**
 * Determines if provider directory location check is required
 * @param userContext - The organization context containing resources
 * @param activeServiceLineName - The active service line name to check against
 * @returns An object containing capability status with reasons for availability
 */
export const getUseProviderDirectoryLocations = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.USE_PROVIDER_DIRECTORY_LOCATIONS,
    activeServiceLineName
  );
};

export const getRelatedCasesFilterCapability = (
  userContext: CMxAPI.OrganizationContext,
  activeServiceLineName: string
) => {
  return determineCapabilityByResource(
    userContext,
    ResourceId.SHOW_RELATED_CASE_FILTER,
    activeServiceLineName
  );
};

